/**
 * Templates feature API layer — OpenAPI-first (§9.1, §9.3).
 *
 * Message templates CRUD.
 */
import {
  getAdminTemplatesListQueryKey,
  useAdminTemplatesCreate as useGenCreate,
  useAdminTemplatesDelete as useGenDelete,
  useAdminTemplatesGet as useGenGet,
  useAdminTemplatesList as useGenList,
  useAdminTemplatesUpdate as useGenUpdate,
} from "@shared/api/generated/admin/admin";
import { useQueryClient } from "@tanstack/react-query";

export type { MessageTemplate } from "@shared/api/generated/model";

// ── Queries ─────────────────────────────────────────────────────────────────

export const useTemplates = () => useGenList({ query: { staleTime: 60_000 } });

export const useTemplate = (id?: string) => {
  const enabled = Boolean(id);
  return useGenGet(id ?? "", { query: { enabled, staleTime: 60_000 } });
};

// ── Mutations ───────────────────────────────────────────────────────────────

export const useCreateTemplate = () => {
  const qc = useQueryClient();
  return useGenCreate({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getAdminTemplatesListQueryKey() });
      },
    },
  });
};

export const useUpdateTemplate = () => {
  const qc = useQueryClient();
  return useGenUpdate({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getAdminTemplatesListQueryKey() });
      },
    },
  });
};

/**
 * Upsert template — delegates to create or update based on presence of `id`.
 * Matches the old useUpsertTemplate() signature for backward compatibility.
 */
export const useUpsertTemplate = () => {
  const qc = useQueryClient();
  const createMut = useGenCreate({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getAdminTemplatesListQueryKey() });
      },
    },
  });
  const updateMut = useGenUpdate({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getAdminTemplatesListQueryKey() });
      },
    },
  });

  return {
    ...createMut,
    mutate: (tpl: { id?: string; [key: string]: unknown }) => {
      if (tpl.id) {
        updateMut.mutate({ id: tpl.id, data: tpl as never });
      } else {
        createMut.mutate({ data: tpl as never });
      }
    },
    mutateAsync: async (tpl: { id?: string; [key: string]: unknown }) => {
      if (tpl.id) {
        return updateMut.mutateAsync({ id: tpl.id, data: tpl as never });
      }
      return createMut.mutateAsync({ data: tpl as never });
    },
    isPending: createMut.isPending || updateMut.isPending,
  };
};

export const useDeleteTemplate = () => {
  const qc = useQueryClient();
  return useGenDelete({
    mutation: {
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: getAdminTemplatesListQueryKey() });
      },
    },
  });
};
